import { debugState, frameNr, time } from './index';
import { COLORS, DEGREE_180_IN_RAD, SHAPE_STYLES, TEXT_STYLE } from './constants';
import {
  addGameObject,
  anchoredPos,
  createTransformAnchoredAnimation,
  createTransformSizeAnimation,
  getAbsolutePos,
  getAbsolutePosAndScale,
  getEntityPositionXY,
  getEntityStateAndProgress,
  getZeroEntity,
  getZeroStaticEntity,
  setEntityPosition,
  setParent,
} from './entity';
import { createAndAddLocalContainer } from './container';
import {
  distanceBetweenSqr,
  divideV2,
  Ease,
  hasVisibleState,
  lerpNumber,
  lerpV2,
  scaleV2,
  noAutoResize,
  normalizeAndMagnitude,
  remapNumber01,
  subtractV2,
  toSize,
  v2,
  addV2,
  getCornerFromRect,
  isEqual,
  cloneTextStyle,
} from './helpers';
import {
  Alignment_Horizontal,
  EventClientToServer,
  JSON_DropAreaAttachEntity,
  JSON_DropAreaDetachEntity,
  ObjectType,
  ServerEvent_DropArea,
  ServerEvent_EntityProps,
} from './shared/api_shared_types';
import { getTextFromWords, setVisibilityState, setParentByEntityProps } from './simulate';
import {
  changeTextObjectText,
  createAndAddLocalTextObject,
  recalculateWordSizes,
} from './textobject/textobject';
import {
  AnimationType,
  Cursor,
  DropArea,
  DropAreaStyle,
  Entity,
  VisibilityState,
  GameManager,
  GameObject,
  GameObjectController,
  MoveMode,
  RenderItemType,
  RenderShapeStyle,
  Shape,
  SharedObjectType,
  V2,
  type TextObjectStyle,
} from './types';
import { createAndAddLocalUserText } from './user_text';
import { stopDragEntity } from './cursor';
import logger from './controllers/logger';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageItem,
  type RenderPackageRect,
  type RenderPackageShape,
} from './render_package';
import { layoutTextObject } from './controllers/text_layout_controller';

const ATTACH_MARGING_W = 15;
const ATTACH_MARGING_H = 15;

const TEXT_INPUT: TextObjectStyle = {
  ...TEXT_STYLE.USER_TEXT,
  color: COLORS.TRANSPARENT,
  colorHighlight: COLORS.WHITE_10,
  defaultTextColor: COLORS.WHITE_100,
};

export function controller(): GameObjectController {
  return {
    getNew: getZeroDropArea,
    sync,
    update,
    createPackage,
  };
}

export function getZeroDropArea(ent: Entity): DropArea {
  let dropArea: DropArea = {
    ...ent,
    objectType: SharedObjectType.DROPAREA,
    acceptObjectTypes: [],
    attachCandidate: null,
    attachedObject: null,
    detachedObject: null,
    previousParent: null,
    willAttachOnDrop: false,
    showLabelOnAttach: false,
    labelAlignment: Alignment_Horizontal.CENTER,

    previousMoveMode: MoveMode.NONE,

    enableDirectInput: false,

    grabber: null,
    grabberTargetPos: v2(),
    strokeColor: [255, 255, 255],
    style: DropAreaStyle.INACTIVE,
    label: '',
    labelTObj: null,
    attachedThisFrame: false,
    detachedThisFrame: false,
    attaching: false,
    attachedTime: 0,
    detachedTime: 0,
    grabberTargetWidth: 0,
    grabberTargetHeight: 0,
    defaultWidth: 0,
    defaultHeight: 0,
    scheduledToUpdateServer: false,
    characterLimit: 0,
    showCharacterLimit: 0,
    showCharacterLimitOnlyWhenEditing: false,
    characterCount: 0,
    charCountTObj: null,
    attachedTextStyle: null,
    originalAttachedTextStyle: null,
    attachedWidthEm: 0,
    originalAttachedWidthEm: 0,

    typeHintOnHover: false,
  };
  dropArea.moveMode = MoveMode.STATIC;
  dropArea.influenceKinetic = false;
  dropArea.allowDrag = false;

  return dropArea;
}

export function sync(manager: GameManager, dropArea: DropArea, props: ServerEvent_DropArea) {
  let entityProps: ServerEvent_EntityProps = {};
  if (props.hasOwnProperty('entityProps')) {
    entityProps = props.entityProps;
  }

  if ('label' in props) {
    dropArea.label = props.label;
  }

  if ('characterLimit' in props) {
    dropArea.characterLimit = props.characterLimit;
  }

  if ('showCharacterLimit' in props) {
    dropArea.showCharacterLimit = props.showCharacterLimit;
  }

  if ('width' in props) {
    dropArea.defaultWidth = props.width || 300;
  }
  if ('height' in props) {
    dropArea.defaultHeight = props.height || 1.5;
  }

  if ('acceptObjectTypes' in props) {
    dropArea.acceptObjectTypes = props.acceptObjectTypes;
  }

  if ('showLabelOnAttach' in props) {
    dropArea.showLabelOnAttach = props.showLabelOnAttach;
  }

  if ('typeHintOnHover' in props) {
    dropArea.typeHintOnHover = props.typeHintOnHover;
  }

  if ('enableDirectInput' in props) {
    dropArea.enableDirectInput = props.enableDirectInput;
  }

  if ('labelAlignment' in props) {
    if (props.labelAlignment !== Alignment_Horizontal.NONE) {
      dropArea.labelAlignment = props.labelAlignment;
    }
  }

  if (!dropArea.id) {
    addGameObject(manager, dropArea);
    initDropArea(manager, dropArea);
    setParentByEntityProps(manager, dropArea, props.sId, props.roomId, entityProps);

    dropArea.style = DropAreaStyle.ACTIVE;
  }

  if ('attachedEntityId' in props) {
    if (props.attachedEntityId > 0) {
      //try attach entity
      if (!dropArea.attachedObject) {
        let gameObjectToAttach = manager.gameObjectServerIdTable[props.attachedEntityId];
        if (!gameObjectToAttach && debugState.isDebugMode) {
          logger.error(
            `Cannot find entity with id ${props.attachedEntityId} to attach to dropArea (serverId: ${dropArea.serverObjId})`,
          );
        } else {
          if (gameObjectToAttach.dragged) {
            stopDragEntity(manager.cursor);
          }
          attachToDropArea(dropArea, gameObjectToAttach, true);
        }
      }
    } else {
      //try detach entity
      if (dropArea.attachedObject) {
        detachFromDropArea(dropArea, true);
      }
    }
  }
}

export function initDropArea(manager: GameManager, dropArea: DropArea) {
  dropArea.shape = Shape.RECT;

  dropArea.influenceKinetic = true;

  {
    const c = createAndAddLocalContainer(manager, getZeroStaticEntity());

    c.width = dropArea.defaultWidth;
    c.height = dropArea.defaultHeight;

    dropArea.grabberTargetWidth = c.width;
    dropArea.grabberTargetHeight = c.height;

    dropArea.width = c.width;
    dropArea.height = c.height;

    dropArea.clickable = true;
    dropArea.allowHighlight = true;
    if (!dropArea.enableDirectInput) {
      dropArea.passClick = true;
    }

    c.clickable = true;
    c.passClick = true;
    c.allowHighlight = true;
    c.passHover = true;

    setParent(c, dropArea, v2());
    dropArea.grabber = c;
  }

  if (dropArea.enableDirectInput) {
    dropArea.attachedTextStyle = TEXT_INPUT;
  }

  if (dropArea.label) {
    const style = dropArea.disableAutoSize
      ? noAutoResize(TEXT_STYLE.LABEL_DROPAREA)
      : TEXT_STYLE.LABEL_DROPAREA;

    dropArea.labelTObj = createAndAddLocalTextObject(
      manager,
      getZeroStaticEntity(),
      dropArea.label,
      style,
      (dropArea.defaultWidth / 100) * 0.8,
    );

    setParent(dropArea.labelTObj, dropArea, v2(0, 0));
    dropArea.labelTObj.moveMode = MoveMode.ANCHORED;
    const relativeX = dropArea.labelAlignment === Alignment_Horizontal.CENTER ? 0.5 : 0;
    const offsetX = dropArea.labelAlignment === Alignment_Horizontal.CENTER ? 0 : 15;
    dropArea.labelTObj.anchoredPos = anchoredPos(
      v2(relativeX, 0.5),
      v2(offsetX, 0),
      v2(relativeX, 0.5),
    );
  }

  const acceptUserText = dropArea.acceptObjectTypes.some((it) => it === ObjectType.USER_TEXT);
  if (acceptUserText && dropArea.characterLimit > 0) {
    dropArea.charCountTObj = createAndAddLocalTextObject(
      manager,
      getZeroStaticEntity(),
      ``,
      TEXT_STYLE.LABEL_DROPAREA_CHARCOUNT,
      2,
    );

    dropArea.charCountTObj.moveMode = MoveMode.ANCHORED;
    dropArea.charCountTObj.anchoredPos = anchoredPos(v2(1, 1), v2(-10, -5), v2(1, 0));

    dropArea.charCountTObj.allowHighlight = true;

    handleDropAreaCharacterLimit(dropArea);

    setParent(dropArea.charCountTObj, dropArea);
  }

  setVisibilityState(dropArea, VisibilityState.APPEARING);
  dropArea.style = DropAreaStyle.ACTIVE;

  return dropArea;
}

export function createAndAddLocalDropArea(
  manager: GameManager,
  ent: Entity,
  label: string,
  acceptObjectTypes: ObjectType[],
  defaultWidth = 200,
  defaultHeight = 50,
  characterLimit = 0,
): DropArea {
  const dropArea = getZeroDropArea(ent);
  addGameObject(manager, dropArea);
  dropArea.label = label;
  dropArea.acceptObjectTypes = acceptObjectTypes;

  dropArea.defaultWidth = defaultWidth;
  dropArea.defaultHeight = defaultHeight;
  dropArea.characterLimit = characterLimit;

  dropArea.disableAutoSize = true;

  initDropArea(manager, dropArea);

  return dropArea;
}

export function attachToDropArea(
  dropArea: DropArea,
  attachedGameObject: GameObject,
  isServerCommand = false,
) {
  if (!dropArea.acceptObjectTypes.some((it) => attachedGameObject.objectType)) {
    console.warn('Object cannot be attached to this DropArea');
  }

  dropArea.attachedObject = attachedGameObject;
  dropArea.attachCandidate = null;
  dropArea.willAttachOnDrop = false;

  dropArea.attachedThisFrame = true;

  attachedGameObject.isAttached = true;

  //check if parent is cursor. This should never happen..
  if (attachedGameObject.parent && attachedGameObject.parent.objectType !== ObjectType.CURSOR) {
    dropArea.previousParent = attachedGameObject.parent;
  } else {
    dropArea.previousParent = dropArea.manager.mainRoom.canvas || dropArea.manager.mainRoom;
  }

  dropArea.previousMoveMode = attachedGameObject.moveMode;

  const toAnchoredPos = dropArea.enableDirectInput
    ? anchoredPos(v2(0, 0.5), v2(0, 0), v2(0, 0.5))
    : anchoredPos(v2(0.5, 0.5), v2(0, 0), v2(0.5, 0.5));
  attachedGameObject.anchoredPos = toAnchoredPos;
  attachedGameObject.moveMode = MoveMode.ANCHORED;
  attachedGameObject.vel.x = 0;
  attachedGameObject.vel.y = 0;
  attachedGameObject.force.x = 0;
  attachedGameObject.force.y = 0;

  setParent(attachedGameObject, dropArea.grabber);

  dropArea.attaching = true;
  dropArea.attachedTime = time;
  dropArea.detachedTime = 0;

  if (dropArea.serverObjId > 0) {
    dropArea.scheduledToUpdateServer = !isServerCommand;
  }

  if (dropArea.labelTObj) {
    if (dropArea.showLabelOnAttach) {
      const relativeX = dropArea.labelAlignment === Alignment_Horizontal.CENTER ? 0.5 : 0;
      const offsetX = dropArea.labelAlignment === Alignment_Horizontal.CENTER ? 0 : 15;

      const relativePos = v2(relativeX, 0);
      const offset = v2(offsetX, -5);
      const relativePivotPos = v2(relativeX, 1);

      createTransformAnchoredAnimation(dropArea.manager, dropArea.labelTObj, {
        type: AnimationType.ANCHORED,
        end: anchoredPos(relativePos, offset, relativePivotPos),
        duration: 0.3,
        easeFunction: Ease.outCubic,
      });
    } else {
      setVisibilityState(dropArea.labelTObj, VisibilityState.HIDING);
    }
  }
  if (dropArea.charCountTObj) {
    handleDropAreaCharacterLimit(dropArea);
  }

  if (attachedGameObject.objectType === ObjectType.USER_TEXT) {
    const userText = attachedGameObject;
    userText.isDropAreaInputMode = true;
    let textIsChanged = false;
    if (dropArea.attachedTextStyle) {
      dropArea.originalAttachedTextStyle = userText.textObject.textStyle;

      userText.textObject.textStyle = cloneTextStyle(dropArea.attachedTextStyle);

      layoutTextObject(userText.textObject);
      textIsChanged = true;
    }

    if (dropArea.attachedWidthEm) {
      dropArea.originalAttachedWidthEm = userText.textObject.maxWidthEm;

      userText.textObject.maxWidthEm = dropArea.attachedWidthEm;
      textIsChanged = true;
    }

    if (textIsChanged) {
      recalculateWordSizes(userText.textObject);
    }
  }
}

export function detachFromDropArea(dropArea: DropArea, isServerCommand = false) {
  const detachedObject = dropArea.attachedObject;

  detachedObject.isAttached = false;

  dropArea.previousParent.attachChild(dropArea.attachedObject, dropArea.previousParent);

  if (!detachedObject.dragged) {
    setEntityPosition(detachedObject, dropArea.pos.x, dropArea.pos.y);
  } else {
    setEntityPosition(detachedObject, dropArea.pos.x, dropArea.pos.y);
    detachedObject.dragPos = detachedObject.parent.relativeMousePos;
  }
  dropArea.attachedObject.moveMode = dropArea.previousMoveMode;
  const absolutePos = getAbsolutePos(dropArea.attachedObject);
  const absolutePosParent = getAbsolutePos(dropArea.previousParent);
  dropArea.attachedObject.anchoredPos.offset.x = absolutePos.x - absolutePosParent.x;
  dropArea.attachedObject.anchoredPos.offset.y = absolutePos.y - absolutePosParent.y;
  dropArea.attachedObject.anchoredPos.relativePos.x = 0;
  dropArea.attachedObject.anchoredPos.relativePos.y = 0;
  dropArea.attachedObject.anchoredPos.relativePivotPos.x = 0;
  dropArea.attachedObject.anchoredPos.relativePivotPos.x = 0;

  dropArea.previousMoveMode = MoveMode.NONE;

  dropArea.detachedObject = detachedObject;
  dropArea.attachedObject = null;
  dropArea.attachedTime = 0;
  dropArea.detachedTime = time;

  dropArea.detachedThisFrame = true;

  dropArea.previousParent = null;

  if (dropArea.serverObjId > 0) {
    dropArea.scheduledToUpdateServer = !isServerCommand;
  }

  if (dropArea.labelTObj) {
    if (dropArea.showLabelOnAttach) {
      const relativeX = dropArea.labelAlignment === Alignment_Horizontal.CENTER ? 0.5 : 0;
      const offsetX = dropArea.labelAlignment === Alignment_Horizontal.CENTER ? 0 : 15;

      const relativePos = v2(relativeX, 0.5);
      const offset = v2(offsetX, 0);
      const relativePivotPos = v2(relativeX, 0.5);

      createTransformAnchoredAnimation(dropArea.manager, dropArea.labelTObj, {
        type: AnimationType.ANCHORED,
        end: anchoredPos(relativePos, offset, relativePivotPos),
        duration: 0.3,
        easeFunction: Ease.outCubic,
      });
    } else {
      setVisibilityState(dropArea.labelTObj, VisibilityState.APPEARING);
    }
  }

  if (dropArea.charCountTObj) {
    // changeTextObjectText(dropArea.charCountTObj, '');
    handleDropAreaCharacterLimit(dropArea);
  }

  if (detachedObject.objectType === ObjectType.USER_TEXT && dropArea.attachedTextStyle) {
    detachedObject.isDropAreaInputMode = false;
    if (detachedObject.text === '') {
      detachedObject.disappearDuration = 0;
    } else {
      detachedObject.textObject.textStyle = dropArea.originalAttachedTextStyle;
      recalculateWordSizes(detachedObject.textObject);
    }
  }

  dropArea.characterCount = 0;
}

function filterDropAreasThatAcceptObjectType(
  dropAreas: DropArea[],
  objectType: ObjectType,
): DropArea[] {
  const result: DropArea[] = [];

  for (let i = dropAreas.length - 1; i >= 0; i--) {
    const it = dropAreas[i];

    if (it.attachedObject) continue;

    const doAcceptObjectType = it.acceptObjectTypes.some((it) => it === objectType);

    if (doAcceptObjectType) {
      result.push(it);
    }
  }

  return result;
}

export function checkDropAreaTargets(manager: GameManager, dropAreas: DropArea[], cursor: Cursor) {
  if (dropAreas.length === 0) return;

  //check droppedEntity
  if (cursor.droppedEntityThisFrame || cursor.canceledDraggingEntityThisFrame) {
    for (let i = dropAreas.length - 1; i >= 0; i--) {
      dropAreas[i].style = DropAreaStyle.ACTIVE;
      if (cursor.canceledDraggingEntityThisFrame) {
        dropAreas[i].attachCandidate = null;
      }
    }
  } else if (cursor.draggedEntity) {
    const candidate = manager.gameObjectIdTable[cursor.draggedEntity.id];

    const objectType = candidate.objectType;

    //filter drop areas that accept this objectType (and are empty)
    const matchingDropAreas = filterDropAreasThatAcceptObjectType(dropAreas, objectType);

    //filter out the drop areas that actually overlap with the object
    // const attachCandidates: DropArea[] = [];
    for (let i = matchingDropAreas.length - 1; i >= 0; i--) {
      const dropArea = matchingDropAreas[i];
      dropArea.attachCandidate = candidate;
      dropArea.style = DropAreaStyle.ACTIVE_HINT;
    }
  }

  if (cursor.draggedEntity || cursor.visibilityState === VisibilityState.DEFAULT) {
    const dropAreasWithCandidate = dropAreas.filter((it) => it.attachCandidate);

    //check if there is and droparea which will grab the object on drop
    //filter out the drop areas that actually overlap with the object

    if (dropAreasWithCandidate.length) {
      const overlappingDropAreas: DropArea[] = [];
      const attachCandidate = dropAreasWithCandidate[0].attachCandidate;
      const [attachCandidateAbsolutePos, attachCandidateScale] =
        getAbsolutePosAndScale(attachCandidate);

      for (let i = dropAreasWithCandidate.length - 1; i >= 0; i--) {
        const dropArea = dropAreasWithCandidate[i];

        dropArea.willAttachOnDrop = false;

        dropArea.style = DropAreaStyle.ACTIVE_HINT;

        const posA = attachCandidateAbsolutePos;
        const widthA = attachCandidate.width * attachCandidateScale;
        const heightA = attachCandidate.height * attachCandidateScale;

        const [posB, scaleB] = getAbsolutePosAndScale(dropArea);
        const widthB = dropArea.width * scaleB;
        const heightB = dropArea.height * scaleB;

        const doOverlap = getRectOverlap(posA, widthA, heightA, posB, widthB, heightB);

        if (doOverlap) {
          overlappingDropAreas.push(dropArea);
        }
      }

      if (overlappingDropAreas.length > 0) {
        //find closest droparea
        let closestDropArea: DropArea = overlappingDropAreas[0];
        if (overlappingDropAreas.length > 1) {
          let closestDistSqr = 100000;
          const posA = attachCandidateAbsolutePos;
          for (let i = overlappingDropAreas.length - 1; i >= 0; i--) {
            const posB = getAbsolutePos(overlappingDropAreas[i]);
            const distSqr = distanceBetweenSqr(posA, posB);
            if (distSqr < closestDistSqr) {
              closestDistSqr = distSqr;
              closestDropArea = overlappingDropAreas[i];
            }
          }
        }
        closestDropArea.willAttachOnDrop = true;
        closestDropArea.style = DropAreaStyle.ACTIVE_HINT_2;

        closestDropArea.grabberTargetWidth =
          closestDropArea.attachCandidate.width + ATTACH_MARGING_W;

        closestDropArea.grabberTargetHeight =
          closestDropArea.attachCandidate.height + ATTACH_MARGING_H;
        closestDropArea.grabberTargetPos = v2(20, 20);

        for (let i = 0; i < dropAreasWithCandidate.length; i++) {
          const dropArea = dropAreasWithCandidate[i];
          const [dropAreaPos, scale] = getAbsolutePosAndScale(dropArea);
          if (dropArea === closestDropArea) {
            dropArea.grabberTargetPos = divideV2(
              subtractV2(attachCandidateAbsolutePos, dropAreaPos),
              scale,
            );
          } else {
            dropArea.grabberTargetPos = v2();
          }
        }
      } else {
        //let all droparea candidates look at object
        // Drop area 'looks at' closest entity
        for (let i = 0; i < dropAreasWithCandidate.length; i++) {
          const dropArea = dropAreasWithCandidate[i];
          const [dropAreaPos, dropAreaScale] = getAbsolutePosAndScale(dropArea);
          const diff = divideV2(subtractV2(attachCandidateAbsolutePos, dropAreaPos), dropAreaScale);
          let { normalize, magnitude } = normalizeAndMagnitude(diff);
          dropArea.grabberTargetPos = scaleV2(
            normalize,
            Math.max(70 - magnitude / 8, 0) * dropAreaScale,
          );
        }
      }
    }
  }

  // for (let i = dropAreas.length - 1; i >= 0; i--) {
  //   updateDropArea(dropAreas[i], cursor.droppedEntityThisFrame);
  // }
}

function handleDropAreaCharacterLimit(dropArea: DropArea) {
  if (!dropArea.charCountTObj) return;

  if (dropArea.attachedObject) {
    if (dropArea.attachedObject.objectType === SharedObjectType.USER_TEXT) {
      const userText = dropArea.attachedObject;
      const charCount = getTextFromWords(userText.textObject.words).length;
      const isEditing = userText.editor ? true : false;
      const highlightChanged =
        dropArea.charCountTObj.highlighted !== dropArea.charCountTObj.highlightLastFrame;
      const charCountChanged = charCount !== dropArea.characterCount;

      if (
        highlightChanged ||
        charCountChanged ||
        userText.startEditingThisFrame ||
        userText.stopEditingThisFrame
      ) {
        setAndCheckCharacterCount(dropArea, charCount, isEditing);
      }
    }
  } else {
    setVisibilityState(dropArea.charCountTObj, VisibilityState.HIDING);
  }
}

function handleUpdateServer(dropArea: DropArea) {
  if (dropArea.attachedObject) {
    let send: JSON_DropAreaAttachEntity;

    if (dropArea.attachedObject.serverObjId > 0) {
      //For all normal gameobjects
      send = {
        dropAreaServerId: dropArea.serverObjId,
        entityServerId: dropArea.attachedObject.serverObjId,
        userId: '',
      };
    }

    if (send) {
      dropArea.scheduledToUpdateServer = false;
    }
  } else {
    if (dropArea.serverObjId) {
      dropArea.scheduledToUpdateServer = false;
    }
  }
}

function getRectOverlap(
  posA: V2,
  widthA: number,
  heightA: number,
  posB: V2,
  widthB: number,
  heightB: number,
): boolean {
  const overlapX1 = posA.x + widthA / 2 >= posB.x - widthB / 2;
  if (!overlapX1) return false;

  const overlapX2 = posA.x - widthA / 2 <= posB.x + widthB / 2;
  if (!overlapX2) return false;

  const overlapY1 = posA.y + heightA / 2 >= posB.y - heightB / 2;
  if (!overlapY1) return false;

  const overlapY2 = posA.y - heightA / 2 <= posB.y + heightB / 2;

  return overlapY2;
}

function update(dropArea: DropArea): boolean {
  const manager = dropArea.manager;

  let isDirty = false;

  const droppedEntityThisFrame = dropArea.manager.cursor.droppedEntityThisFrame;

  let { state, progress } = getEntityStateAndProgress(dropArea);

  if (state === VisibilityState.APPEARING) {
    let minHeightScale = 32 / dropArea.height;
    let growHeightScale = 1 - minHeightScale;
    let t = remapNumber01(progress, 0.5, 1, true);
    dropArea.scale = minHeightScale + t * growHeightScale;
    // dropArea.widthScale = remapNumber01(progress, 0, 0.5, true);
  } else if (state === VisibilityState.DISAPPEARING || state === VisibilityState.HIDING) {
    let minHeightScale = 32 / dropArea.height;
    let schrinkHeightScale = 1 - minHeightScale;
    let t = remapNumber01(progress, 0, 0.5, true);
    dropArea.scale = 1 - t * schrinkHeightScale;
    // dropArea.widthScale = 1 - remapNumber01(progress, 0.5, 1, true);
  } else if (state === VisibilityState.DEFAULT) {
    if (dropArea.scale !== 1) {
      dropArea.scale = 1;
      // dropArea.heightScale = 1;
    }
  } else if (state === VisibilityState.HIDDEN) {
    if (dropArea.scale !== 0) {
      dropArea.scale = 0;
      // dropArea.heightScale = 0;
    }
  }

  if (
    dropArea.attachCandidate &&
    dropArea.attachCandidate.visibilityState === VisibilityState.DISAPPEARING
  ) {
    dropArea.attachCandidate = null;
  }

  if (
    dropArea.attachedObject &&
    dropArea.attachedObject.visibilityState === VisibilityState.DISAPPEARING
  ) {
    detachFromDropArea(dropArea, false);
  }

  if (droppedEntityThisFrame) {
    if (dropArea.attachCandidate && dropArea.willAttachOnDrop) {
      if (droppedEntityThisFrame.id == dropArea.attachCandidate.id) {
        attachToDropArea(dropArea, dropArea.attachCandidate);
        if (dropArea.enableDirectInput) {
          dropArea.grabberTargetWidth = dropArea.width;
        }
      }
    }
    dropArea.style = DropAreaStyle.ACTIVE;
    dropArea.attachCandidate = null;
  }

  if (dropArea.attachedThisFrame) {
    if (!dropArea.enableDirectInput) {
      dropArea.grabberTargetWidth = dropArea.attachedObject.width + ATTACH_MARGING_W;
    }
    dropArea.grabberTargetHeight = dropArea.attachedObject.height + ATTACH_MARGING_H;
  } else if (!dropArea.willAttachOnDrop && !dropArea.attachedObject) {
    dropArea.grabberTargetWidth = dropArea.defaultWidth;
    dropArea.grabberTargetHeight = dropArea.defaultHeight;
  }

  if (dropArea.detachedThisFrame) {
    const toWidth = dropArea.defaultWidth;
    const toHeight = dropArea.defaultHeight;

    createTransformSizeAnimation(dropArea.manager, dropArea, {
      type: AnimationType.TRANSFORM_SIZE,
      end: {
        sizePx: toSize(toWidth, toHeight),
        sizeEm: null,
        // sizeScale: toSize(1, 1),
      },
      duration: 0.4,
      easeFunction: Ease.outBack,
    });
  }

  if (!dropArea.attachCandidate) {
    if (dropArea.attachedObject && dropArea.enableDirectInput) {
      const toAnchoredPos = anchoredPos(v2(0, 0.5), v2(0, 0), v2(0, 0.5));
      const [x, y] = getEntityPositionXY(dropArea.grabber, toAnchoredPos);
      dropArea.grabberTargetPos = v2(x, y);
    } else {
      dropArea.grabberTargetPos = v2();
    }
  }

  dropArea.grabber.width = lerpNumber(dropArea.grabber.width, dropArea.grabberTargetWidth, 0.3);
  dropArea.grabber.height = lerpNumber(dropArea.grabber.height, dropArea.grabberTargetHeight, 0.3);

  if (dropArea.attachedObject) {
    if (!dropArea.enableDirectInput) {
      dropArea.grabberTargetWidth = dropArea.attachedObject.width + ATTACH_MARGING_W;
    }
    dropArea.grabberTargetHeight = dropArea.attachedObject.height + ATTACH_MARGING_H;

    const width = lerpNumber(dropArea.width, dropArea.grabberTargetWidth, 0.3, 0.1);
    const height = lerpNumber(dropArea.height, dropArea.grabberTargetHeight, 0.3, 0.1);

    if (dropArea.width !== width || dropArea.height !== height) {
      if (!dropArea.enableDirectInput) {
        dropArea.width = width;
      }

      dropArea.height = height;
      isDirty = true;
    }
  } else {
    if (dropArea.manager.space.layoutChanged) {
      const width = dropArea.defaultWidth;
      const height = dropArea.defaultHeight;

      if (dropArea.width !== width || dropArea.height !== height) {
        dropArea.width = width;
        dropArea.height = height;
        isDirty = true;
      }
    }
    if (dropArea.grabberTargetWidth !== dropArea.width) {
      dropArea.grabberTargetWidth = dropArea.defaultWidth;
      dropArea.grabberTargetHeight = dropArea.defaultHeight;
    }
  }

  const newPos = lerpV2(dropArea.grabber.pos, dropArea.grabberTargetPos, 0.3);
  if (!isEqual(newPos, dropArea.grabber.pos)) {
    setEntityPosition(dropArea.grabber, newPos.x, newPos.y);
    dropArea.layoutChanged = true;
  }

  const acceptUserText = dropArea.acceptObjectTypes.some((it) => it === ObjectType.USER_TEXT);
  if (dropArea.attachedObject && acceptUserText && dropArea.characterLimit > 0) {
    handleDropAreaCharacterLimit(dropArea);
  }

  if (dropArea.scheduledToUpdateServer) {
    handleUpdateServer(dropArea);
  }

  if (dropArea.typeHintOnHover) {
    dropArea.showTypeHintOnHover = dropArea.attachedObject === null;
  }

  if (dropArea.enableDirectInput) {
    if (
      state === VisibilityState.DEFAULT &&
      !dropArea.attachedObject &&
      dropArea.clickedThisFrame
    ) {
      const userText = createAndAddLocalUserText(
        dropArea.manager,
        getZeroEntity(),
        '',
        false,
        TEXT_STYLE.USER_TEXT,
        3,
      );

      userText.requestSyncToServer = true;
      userText.requestStartEditMode = true;
      userText.appearDuration = 0;

      attachToDropArea(dropArea, userText);
    }
  }

  return isDirty;
}

export function checkDetachFromDropAreas(dropAreas: DropArea[], cursor: Cursor) {
  for (let i = 0; i < dropAreas.length; i++) {
    let dropArea = dropAreas[i];
    if (dropArea.attachedObject) {
      if (
        dropArea.attachedObject.dragged &&
        dropArea.manager.cursor.draggedEntity === dropArea.attachedObject
      ) {
        detachFromDropArea(dropArea);
      }
    }
  }
}

function setAndCheckCharacterCount(dropArea: DropArea, charCount: number, isEditing: boolean) {
  const limitExceeded = charCount > dropArea.characterLimit;
  dropArea.characterCount = charCount;

  const textStyle = limitExceeded
    ? TEXT_STYLE.LABEL_DROPAREA_CHARCOUNT_ERR
    : TEXT_STYLE.LABEL_DROPAREA_CHARCOUNT;

  dropArea.charCountTObj.textStyle = cloneTextStyle(textStyle);
  const text = dropArea.charCountTObj.highlighted
    ? `character limit: ${dropArea.characterLimit}`
    : `${charCount}/${dropArea.characterLimit}`;
  changeTextObjectText(dropArea.charCountTObj, text);

  const forceHide = !limitExceeded && dropArea.showCharacterLimitOnlyWhenEditing && !isEditing;

  if (
    !forceHide &&
    charCount >= dropArea.showCharacterLimit &&
    !hasVisibleState(dropArea.charCountTObj.visibilityState)
  ) {
    setVisibilityState(dropArea.charCountTObj, VisibilityState.APPEARING);
  } else if (
    (forceHide || charCount < dropArea.showCharacterLimit) &&
    hasVisibleState(dropArea.charCountTObj.visibilityState)
  ) {
    setVisibilityState(dropArea.charCountTObj, VisibilityState.HIDING);
  }
}

export function resetDropAreas(dropAreas: DropArea[]) {
  for (let i = dropAreas.length - 1; i >= 0; i--) {
    dropAreas[i].detachedThisFrame = false;
    dropAreas[i].attachedThisFrame = false;
    dropAreas[i].detachedObject = null;
  }
}

export function isObjectAttachedToDropArea(obj): DropArea {
  if (obj.parent && obj.parent.parent && obj.parent.parent.objectType === ObjectType.DROPAREA) {
    return obj.parent.parent;
  }

  return null;
}

export function createPackage(
  dropArea: DropArea,
  position: V2,
  scale: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  let shapeStyle: RenderShapeStyle = SHAPE_STYLES.NONE;

  if (dropArea.style === DropAreaStyle.ACTIVE) {
    shapeStyle = SHAPE_STYLES.DROP_ACTIVE;
  } else if (dropArea.style === DropAreaStyle.ACTIVE_HINT) {
    shapeStyle = SHAPE_STYLES.DROP_ACTIVE_HINT;
  } else if (dropArea.style === DropAreaStyle.ACTIVE_HINT_2) {
    shapeStyle = SHAPE_STYLES.DROP_ACTIVE_HINT_2;
  }

  {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      width: dropArea.width,
      height: dropArea.height,
      shapeStyle: SHAPE_STYLES.DROP_BACK,
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };
    rPackage.push(rect);
  }

  if (true) {
    const CORNER_SIZE = 10;
    const pos = addV2(position, scaleV2(dropArea.grabber.pos, scale));
    for (let i = 0; i < 2; i++) {
      const corner = i * 2;
      const cornerPos = getCornerFromRect(dropArea.grabber.width, dropArea.grabber.height, corner);
      const shapePos = addV2(pos, scaleV2(cornerPos, scale));

      let shape: RenderPackageShape = {
        ...EMPTY_RENDER_TRANSFORM,
        type: RenderItemType.shape,
        positionX: shapePos.x,
        positionY: shapePos.y,
        rotation: i * DEGREE_180_IN_RAD,
        points: [
          v2(CORNER_SIZE * scale, dropArea.grabber.height * scale),
          v2(0, dropArea.grabber.height * scale),
          v2(),
          v2(CORNER_SIZE * scale, 0),
        ],
        fill: false,
        close: false,
        shapeStyle,
        shadow: null,
      };
      rPackage.push(shape);
    }
  }

  return rPackage;
}

export function isAttachedToDropArea(ent: Entity): boolean {
  if (ent && ent.parent && ent.parent.parent) {
    return ent.parent.parent.objectType === ObjectType.DROPAREA;
  }

  return false;
}
